import type { RoomOptionModel } from '@elara/client-core/models/RoomOptionModel';

import type { RoomSearchFormProps } from './room-search-form.js';

import { formatDistanceToNow } from 'date-fns';
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

import { roomShortlistMetadata } from '@elara/client-core/lib/room-utils';
import { cn, pluralize } from '@elara/client-core/lib/utils';
import { ROOM_SEARCH_AGED_AFTER_MS } from '@elara/client-core/states/HotelRoomSearchState';

import { useHotelRoomSearchState, useRouterState, useTripsState } from '#states/hooks.js';
import { Alert, AlertDescription } from '#ui/alert.js';
import { Button } from '#ui/button.js';
import { RoomCard } from '#ui/cards/RoomCard.js';
import { RoomPickBadge, RoomPickReason } from '#ui/cards/RoomPick.js';
import { EmptyState } from '#ui/EmptyState.js';
import Loader from '#ui/loader.js';
import { MaterialIcon } from '#ui/MaterialIcon.js';
import { ShortlistButton } from '#ui/shortlist-button.js';
import { ThinkingText } from '#ui/thinking-text.js';

import { useOpenGuide } from './guide-scope.js';
import { RoomSearchBar } from './RoomSearchBar.js';
import { RoomSearchPanels } from './RoomSearchPanels.js';

/**
 * A result as the chat carousel draws a room (`RecommendationWidget`): the `feature` card with no surface
 * of its own, and the halo that lifts it on hover — a white panel with a shadow that grows 12px past the
 * card as it fades in, as the whole card opens the room guide. `isolate` keeps the halo's `-z-10`
 * behind this card rather than behind the tab's band.
 */
const ROOM_RESULT_CARD = cn(
  'relative isolate w-full rounded-md',
  'before:absolute before:-z-10 before:rounded-lg before:bg-background before:shadow-md',
  'before:inset-0 before:opacity-0 before:transition-[inset,opacity] before:duration-300 before:ease-out',
  'hover:before:-inset-3 hover:before:opacity-100',
);

/** Elara's picks: one column in a narrow guide, more as it widens. The gaps leave the halo room to grow. */
const ROOM_RESULT_GRID = 'grid grid-cols-1 gap-x-4 gap-y-8 @xl:grid-cols-2 @4xl:grid-cols-3';

/**
 * The skeleton's cards, one per column `ROOM_RESULT_GRID` can have, each shown from the width its column
 * appears at — so the skeleton always fills the grid's first row and never starts a second. Change the
 * two together.
 */
const SKELETON_CARD_VISIBILITY = ['flex', 'hidden @xl:flex', 'hidden @4xl:flex'];

/** The rest of the rooms, denser than the picks: three across once the guide has the width. */
const MORE_ROOMS_GRID = 'grid grid-cols-1 gap-x-4 gap-y-8 @md:grid-cols-2 @xl:grid-cols-3 @4xl:grid-cols-4';

/**
 * The guide is the hotel's own, so the hotel needs no repeating per room. How many each room sleeps stays:
 * it is what tells a family which rooms take them in one and which they would need two of.
 */
const OMITTED_ROOM_FACTS = ['hotel'] as const;

/**
 * Runs `update` as a view transition where the browser has them: the page cross-fades from before to
 * after rather than cutting. Nothing is named for the transition, so it is the browser's default fade —
 * the open form fading out as the bar fades in, everything that did not change holding still. (Naming
 * the two forms alike made the open form morph into the bar, which read awkwardly between such different
 * shapes.) `update` must change the DOM synchronously (`flushSync`). Without the API, or with reduced
 * motion asked for, it just runs.
 */
function withViewTransition(update: () => void): void {
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  if (typeof document.startViewTransition !== 'function' || reduceMotion) {
    update();
    return;
  }
  document.startViewTransition(update);
}

export interface HotelRoomSearchProps {
  hotelId: string;
  /** Open on this search's rooms (a "View all rooms" link); otherwise the hotel's last search. */
  searchId?: string;
}

/**
 * The hotel guide's Rooms tab (ELARA-1092): the traveller searches this hotel's rooms themselves.
 *
 * The form opens pre-filled (the hotel's last search, the trip's, the concierge's working memory, or
 * the trip dates). A search runs the concierge's own room search, curated picks included, and every
 * room it saves is listed from Firestore: Elara's picks first, with why each was picked, then the
 * rest by price. Results opened from a chat card are the saved ones, never a fresh search; Refresh
 * re-prices them in place.
 */
export function HotelRoomSearch({ hotelId, searchId }: HotelRoomSearchProps) {
  const state = useHotelRoomSearchState();
  const routerState = useRouterState();
  const tripsState = useTripsState();

  // Set by the first search from this tab: from then on the form sits at the top, above the results.
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    state.open(hotelId, { searchId });
    setHasSearched(false);
    // `state` is a stable DI reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId, searchId]);

  const { form, phase } = state;
  const isLoading = phase.kind === 'loading';
  // Fades in whatever replaces the loading state, once: cleared when that fade ends, so the first
  // search's own view transition is not doubled by a second fade on the results.
  const [revealAfterLoad, setRevealAfterLoad] = useState(false);
  useEffect(() => {
    if (isLoading) setRevealAfterLoad(true);
  }, [isLoading]);
  const revealClass = revealAfterLoad ? 'animate-in fade-in duration-500' : undefined;
  const endReveal = (event: React.AnimationEvent) => {
    if (event.target === event.currentTarget) setRevealAfterLoad(false);
  };
  const isSearching = phase.kind === 'searching';
  const hasResults = state.rooms.length > 0;
  // Nothing searched yet and nothing saved to show: the form is the whole tab, centred and stacked.
  // Also while the pre-fill loads, so a first visit does not see the form jump to the middle; a saved
  // search it finds brings its rooms in with the form at the top.
  const isFirstSearch = !hasSearched && !hasResults && !state.activeSearchId && phase.kind === 'ready';
  const formNote = (state.formTouched || !form.startDate) && state.formError ? state.formError : undefined;

  const runSearch = () => {
    // The URL's `search` names the search the tab was opened on. Once the traveller searches again it
    // is stale; dropped, a reload lands on the hotel's latest search, which is this one.
    if (routerState.getHashParam('search')) tripsState.replaceGuide('hotel', hotelId, { tab: 'rooms' });
    const submit = () => {
      flushSync(() => setHasSearched(true));
      void state.search();
    };
    // The first search fades the open form out and the bar above the results in; the skeleton follows.
    if (isFirstSearch) withViewTransition(submit);
    else submit();
  };

  const formProps: RoomSearchFormProps = {
    startDate: form.startDate,
    endDate: form.endDate,
    occupancy: form.occupancy,
    prefillKey: `${state.hotelId ?? ''}:${state.prefillVersion}`,
    onDatesChange: (start, end) => state.setDates(start, end),
    onOccupancyChange: (occupancy) => state.setOccupancy(occupancy),
    onSearch: runSearch,
    canSearch: state.canSearch,
    isSearching,
    disabled: isSearching,
  };
  // Only says something when it explains a disabled form or button.
  const formNoteLine = formNote && <p className="m-0 text-sm text-muted-foreground">{formNote}</p>;

  // The pre-fill brings the stay, the party and the hotel's last search, which decides whether the
  // tab opens on the form or on that search's rooms: nothing is drawn until it lands, so the form
  // never shows the defaults and then changes under the traveller.
  if (isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading your trip details"
        className="flex flex-1 flex-col items-center justify-center gap-4 py-8"
      >
        <Loader thinking size={56} />
        <ThinkingText text="Fetching your trip details" />
      </div>
    );
  }

  if (isFirstSearch) {
    return (
      // The empty tab is the form alone, opened out: its pickers on screen rather than in popovers.
      // The note under the form (why Find rooms waits) sits as close to it as the form's own parts do.
      <section
        aria-label="Search rooms"
        className={cn('flex flex-1 flex-col items-center justify-center gap-3 py-8', revealClass)}
        onAnimationEnd={endReveal}
      >
        <RoomSearchPanels {...formProps} />
        {formNoteLine}
      </section>
    );
  }

  return (
    // Fills the tab, so a lone empty state or search stage centres in the space below the form.
    <div className={cn('flex flex-1 flex-col gap-6', revealClass)} onAnimationEnd={endReveal}>
      <section aria-label="Search rooms" className="flex flex-col gap-2">
        {/* The search's streamed stage sits beside the bar that started it, below it in a narrow guide. */}
        <div className="flex flex-col gap-2 @xl:flex-row @xl:items-center @xl:gap-4">
          <RoomSearchBar {...formProps} />
          {isSearching && (
            <p role="status" aria-live="polite" className="m-0 min-w-0 text-sm text-muted-foreground">
              {phase.progress?.message ?? (phase.mode === 'refresh' ? 'Updating prices…' : 'Starting your search…')}
            </p>
          )}
        </div>
        {formNoteLine}
        {/* Under the form that searched them: how fresh the rooms below are, and the way to re-price them. */}
        {hasResults && !isSearching && <RatesCheckedLine />}
      </section>

      {/* While a search or refresh runs, the results stand in outline: the rooms listed would be the
          previous stay's, or about to be re-priced. Its stage is beside the search bar. */}
      {isSearching && <RoomResultsSkeleton />}

      {phase.kind === 'empty' && !hasResults && (
        <EmptyState className="py-8" title="No rooms to show" description={phase.message} />
      )}
      {/* A refresh that could not reach the hotel keeps its rooms listed, so it says so above them. */}
      {phase.kind === 'empty' && hasResults && (
        <Alert>
          <MaterialIcon icon="info" />
          <AlertDescription>{phase.message}</AlertDescription>
        </Alert>
      )}
      {phase.kind === 'error' && (
        <Alert variant="destructive">
          <MaterialIcon icon="error" />
          <AlertDescription>{phase.message}</AlertDescription>
        </Alert>
      )}

      {state.activeSearchId && !state.isResultsReady && !isSearching && (
        <div role="status" aria-label="Loading rooms">
          <RoomResultsSkeleton />
        </div>
      )}

      {state.picks.length > 0 && !isSearching && (
        <section aria-label="Elara's picks" className={ROOM_RESULT_GRID}>
          {state.picks.map((room) => (
            <PickedRoom key={room.id} room={room} />
          ))}
        </section>
      )}

      {state.otherRooms.length > 0 && !isSearching && (
        <section aria-label="All rooms" className="flex flex-col gap-6">
          {/* A quiet divider rather than a heading: small and muted, ruled out to its right. */}
          <div className="flex items-center gap-3">
            <h3 className="m-0 shrink-0 text-sm font-medium text-muted-foreground">
              {state.picks.length > 0
                ? 'More rooms'
                : `${state.otherRooms.length} available ${pluralize(state.otherRooms.length, 'room')}`}
            </h3>
            <div aria-hidden className="h-px flex-1 bg-border" />
          </div>
          <div className={MORE_ROOMS_GRID}>
            {state.otherRooms.map((room) => (
              // Smaller titles than the picks': these cards sit three across, and are the quieter list.
              <ResultRoom key={room.id} room={room} titleClassName="text-sm" />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * The results while a search runs or saved rooms load: the grid's first row of cards in outline — photo,
 * name, facts, the price and a few lines of why — so the rooms arrive into the space they will take.
 */
function RoomResultsSkeleton() {
  return (
    <div className={ROOM_RESULT_GRID} aria-hidden>
      {SKELETON_CARD_VISIBILITY.map((visibility, index) => (
        <div key={index} className={cn(visibility, 'flex-col gap-2.5')}>
          {/* The cover's own empty colour (`CoverCarousel`): a step darker than the tab's band it sits on. */}
          <div className="aspect-[3/2] w-full animate-pulse rounded-md bg-background-quaternary" />
          <SkeletonLine className="mt-1.5 h-5 w-2/3" />
          <SkeletonLine className="w-1/2" />
          <SkeletonLine className="mt-1.5 h-4 w-1/4" />
          <SkeletonLine className="mt-1.5 w-full" />
          <SkeletonLine className="w-full" />
          <SkeletonLine className="w-2/3" />
        </div>
      ))}
    </div>
  );
}

function SkeletonLine({ className }: { className?: string }) {
  return <div className={cn('h-3 animate-pulse rounded bg-background-tertiary', className)} />;
}

/**
 * "Availability and rates checked 3 hours ago", a Refresh link, and a nudge once the rates are a day
 * old. Hidden while the rates are fresh: the traveller has just searched.
 */
function RatesCheckedLine() {
  const state = useHotelRoomSearchState();
  const checkedAt = state.ratesCheckedAt;
  const isRefreshing = state.phase.kind === 'searching' && state.phase.mode === 'refresh';
  const [, rerender] = useState(0);

  // Age is not a signal, so re-render once when fresh rates cross the threshold.
  const checkedAtMs = checkedAt?.getTime();
  useEffect(() => {
    if (checkedAtMs === undefined) return;
    const remaining = checkedAtMs + ROOM_SEARCH_AGED_AFTER_MS - Date.now();
    if (remaining <= 0) return;
    const timer = setTimeout(() => rerender((n) => n + 1), remaining + 1000);
    return () => clearTimeout(timer);
  }, [checkedAtMs]);

  if (!state.areRatesAged()) return null;
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-muted-foreground">
      <span>
        {checkedAt
          ? `Availability and rates checked ${formatDistanceToNow(checkedAt, { addSuffix: true })}`
          : 'Saved availability and rates'}
        {state.areRatesStale() && ' · prices may have changed'}
      </span>
      <Button
        variant="link"
        onClick={() => void state.refresh()}
        disabled={!state.canRefresh}
        aria-label="Refresh availability and rates"
        className="text-xs"
      >
        {isRefreshing ? 'Refreshing…' : 'Refresh'}
      </Button>
    </div>
  );
}

/** One of Elara's picks: the card priced on the picked rate, with why it was picked. */
function PickedRoom({ room }: { room: RoomOptionModel }) {
  const pick = room.recommendedRate;
  const recommendation = pick?.recommendation;
  return (
    <ResultRoom
      room={room}
      productCode={pick?.productCode}
      occupancy={pick?.occupancy}
      // "Book 2" beside the name when the party needs more than one of the room; the price line then totals them.
      quantity={room.roomsToBook(pick?.occupancy)}
      coverBadge={recommendation && <RoomPickBadge recommendation={recommendation} />}
      note={recommendation && <RoomPickReason recommendation={recommendation} />}
    />
  );
}

/** A room card that opens the room guide, with a shortlist heart for the room. */
function ResultRoom({
  room,
  productCode,
  occupancy,
  coverBadge,
  note,
  titleClassName,
  quantity,
}: {
  room: RoomOptionModel;
  productCode?: string;
  occupancy?: RoomOptionModel['rates'][number]['occupancy'];
  coverBadge?: React.ReactNode;
  /** Classes for the card's title, e.g. a smaller one in the denser grid. */
  titleClassName?: string;
  note?: React.ReactNode;
  /** How many of the room the party books. */
  quantity?: number;
}) {
  // Inside the guide, so the room guide is pushed: Back returns to these results.
  const openGuide = useOpenGuide();
  const rate = productCode ? room.getRateByIdentity({ productCode, occupancy }) : (room.leadRate ?? room.cheapestRate);
  const id = room.id;
  if (!id) return null;
  return (
    <RoomCard
      className={ROOM_RESULT_CARD}
      room={room}
      productCode={rate?.productCode}
      occupancy={rate?.occupancy}
      pricePartyLabel="when-different"
      size="feature"
      omitFacts={OMITTED_ROOM_FACTS}
      coverBadge={coverBadge}
      titleClassName={titleClassName}
      note={note}
      quantity={quantity}
      onClick={() => openGuide('room', id)}
      actions={
        <ShortlistButton
          itemType="room"
          referenceId={id}
          title={room.displayName}
          // The room, unpinned, like every app shortlist button: its card leads with the lowest bookable
          // rate for the searched party.
          metadata={roomShortlistMetadata(room)}
          variant="icon"
          size="icon-xs"
        />
      }
    />
  );
}
